import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useTranslation } from "react-i18next";
import { PredefinedObstacle } from "../../../../hooks/usePredefinedObstacle";
import { DimensionInput } from "./DimensionInput";
import { EditFormData } from "../index";

interface Props {
  open: boolean;
  obstacle: PredefinedObstacle | null;
  isCreating: boolean;
  onClose: () => void;
  onSave: (data: EditFormData) => Promise<void>;
}

export const ObstacleEditDialog: React.FC<Props> = ({
  open,
  obstacle,
  isCreating,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = React.useState<EditFormData>({
    name: "",
    width: "",
    length: "",
    height: "",
    image: undefined,
  });
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [imageError, setImageError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Reset form when obstacle changes or creating new
  React.useEffect(() => {
    if (isCreating) {
      setFormData({
        name: "",
        width: "",
        length: "",
        height: "",
        image: undefined,
      });
      setImagePreview(null);
      setImageError(null);
    } else if (obstacle) {
      setFormData({
        name: obstacle.name,
        width: "",
        length: "",
        height: "",
        image: undefined,
      });
      setImagePreview(null);
      setImageError(null);
    }
  }, [obstacle, isCreating]);

  const handleChange = (field: keyof EditFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Clear previous error
      setImageError(null);
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setImageError(t('superAdmin.obstacleEdit.errors.fileSizeLimit'));
        event.target.value = ''; // Clear the input
        return;
      }

      // Validate file type (images only)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        setImageError(t('superAdmin.obstacleEdit.errors.fileTypeInvalid'));
        event.target.value = ''; // Clear the input
        return;
      }
      
      setFormData(prev => ({ ...prev, image: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    await onSave(formData);
    // Don't close here - let parent close on success
  };

  const handleClose = () => {
    setFormData({ name: "", width: "", length: "", height: "", image: undefined });
    setImagePreview(null);
    setImageError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isCreating ? t('superAdmin.obstacleEdit.title.create') : t('superAdmin.obstacleEdit.title.edit')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label={t('superAdmin.obstacleEdit.labels.name')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder={obstacle?.name || t('superAdmin.obstacleEdit.placeholders.name.create')}
            autoFocus
            required={isCreating}
          />
          <Divider />
          {(obstacle || isCreating) && (
            <>
              <DimensionInput
                label={t('superAdmin.obstacleEdit.labels.width')}
                currentValue={obstacle?.width}
                value={formData.width}
                onChange={(value) => handleChange('width', value)}
                placeholder={isCreating ? t('superAdmin.obstacleEdit.placeholders.width.create') : t('superAdmin.obstacleEdit.placeholders.width.edit')}
                required={isCreating}
              />
              <DimensionInput
                label={t('superAdmin.obstacleEdit.labels.length')}
                currentValue={obstacle?.length}
                value={formData.length}
                onChange={(value) => handleChange('length', value)}
                placeholder={isCreating ? t('superAdmin.obstacleEdit.placeholders.length.create') : t('superAdmin.obstacleEdit.placeholders.length.edit')}
                required={isCreating}
              />
              <DimensionInput
                label={t('superAdmin.obstacleEdit.labels.height')}
                currentValue={obstacle?.height}
                value={formData.height}
                onChange={(value) => handleChange('height', value)}
                placeholder={isCreating ? t('superAdmin.obstacleEdit.placeholders.height.create') : t('superAdmin.obstacleEdit.placeholders.height.edit')}
                required={isCreating}
              />
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('superAdmin.obstacleEdit.labels.obstacleImage')}
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  fullWidth
                  color={imageError ? "error" : "primary"}
                >
                  {formData.image ? t('superAdmin.obstacleEdit.image.changeButton') : t('superAdmin.obstacleEdit.image.uploadButton')}
                </Button>
                {imageError && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {imageError}
                  </Typography>
                )}
                {formData.image && !imageError && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    {t('superAdmin.obstacleEdit.image.selected', { filename: formData.image.name })}
                  </Typography>
                )}
                {imagePreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                      src={imagePreview}
                      alt={t('superAdmin.obstacleEdit.image.preview')}
                      style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Stack>
        {!isCreating && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            {t('superAdmin.obstacleEdit.helperText.keepCurrent')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('superAdmin.obstacleEdit.buttons.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">
          {isCreating ? t('superAdmin.obstacleEdit.buttons.create') : t('superAdmin.obstacleEdit.buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};